/**
 * Guías Habitia — las guías públicas de la web (habitia.ai/<ruta>) disponibles
 * en Materiales para que el comercial las comparta: copiar el link o exportar
 * en PDF/HTML con el MISMO diseño de la landing (MoneyPage).
 *
 * FUENTE CANÓNICA del contenido: habitia_stio_presentacion/src/data/moneyPages.js
 * (copiado 1:1 — si la web cambia, re-sincronizar a mano este archivo).
 * En el export, los CTA de WhatsApp apuntan al COMERCIAL (regla del canal:
 * el lead es suyo), nunca al número general de Habitia.
 */

// FT Habit para el export a PDF (misma receta que flyer/deck/bloques).
import ftBook from "../assets/fonts/FTHabitUnlicensedTrial-Book.otf";
import ftRegular from "../assets/fonts/FTHabitUnlicensedTrial-Regular.otf";
import ftMedium from "../assets/fonts/FTHabitUnlicensedTrial-Medium.otf";

export const TIPO_GUIA = "guia_web";

// Meta de cada guía (ruta pública + label del menú de la web, seoRoutes.js).
export const GUIAS_META = [
  { path: "/administracion-de-edificios-montevideo", navLabel: "Administración de edificios en Montevideo" },
  { path: "/cuanto-cobra-una-administracion", navLabel: "¿Cuánto cobra una administración?" },
  { path: "/como-cambiar-de-administracion", navLabel: "Cómo cambiar de administración" },
  { path: "/software-de-administracion-de-edificios", navLabel: "Software de administración de edificios" },
  { path: "/gastos-comunes-que-incluyen", navLabel: "Qué incluyen los gastos comunes" },
  { path: "/morosidad-gastos-comunes", navLabel: "Morosidad en gastos comunes" },
  { path: "/asamblea-de-copropietarios", navLabel: "Asambleas de copropietarios" },
];

// ── Contenido (copiado 1:1 de moneyPages.js de la landing — NO editar acá) ──
export const GUIAS_CONTENT = {
  '/gastos-comunes-que-incluyen': {
    h1: 'Gastos comunes: qué incluyen y cómo se calculan',
    lead: 'Los gastos comunes son la cuota mensual que mantiene vivo al edificio — y también la principal fuente de dudas y discusiones entre vecinos. Esta guía explica qué incluyen exactamente, cómo se reparten entre las unidades, por qué suben y qué se puede hacer para bajarlos sin recortar servicios.',
    fecha: '2026-08-17',
    sections: [
      {
        heading: 'Qué incluyen los gastos comunes',
        body: ['Aunque cada edificio es distinto, la cuota mensual suele cubrir:'],
        list: [
          'Limpieza de las áreas comunes y retiro de residuos.',
          'Luz y agua de los espacios compartidos (pasillos, garage, jardín).',
          'Mantenimiento de ascensores, bombas de agua y portones.',
          'Seguro del edificio.',
          'Sueldos y cargas sociales del personal (portería, limpieza), si lo hay.',
          'Honorarios de la administración.',
          'Fondo de reserva para reparaciones e imprevistos.',
        ],
      },
      {
        heading: 'Cómo se dividen entre las unidades',
        body: [
          'La regla la fija el reglamento de copropiedad de cada edificio. Lo más común en Uruguay es el prorrateo por porcentaje o coeficiente: cada unidad paga según su proporción sobre el total del edificio (que suele reflejar el metraje). Algunos reglamentos definen cuotas iguales para todas las unidades o criterios mixtos — por ejemplo, dividir el ascensor solo entre los pisos altos.',
          'Por eso dos apartamentos del mismo edificio pueden pagar cuotas distintas, y por eso es clave que la liquidación mensual muestre el detalle: qué se gastó, en qué, y cómo se repartió.',
        ],
      },
      {
        heading: 'Por qué suben los gastos comunes',
        body: ['Las causas más frecuentes de aumento:'],
        list: [
          'Ajustes de tarifas (UTE, OSE) y de sueldos del personal.',
          'Mantenimiento postergado que termina en reparaciones caras.',
          'Morosidad: cuando algunos vecinos no pagan, el costo del edificio se sigue cubriendo entre menos unidades.',
          'Contratos de servicios que nunca se revisan ni se vuelven a cotizar.',
        ],
      },
      {
        heading: 'Cómo bajarlos sin recortar servicios',
        body: [
          'La palanca más grande no es recortar: es gestionar. Revisar y re-cotizar contratos (limpieza, ascensores, seguros) cada año, controlar consumos de áreas comunes, y sobre todo atacar la [morosidad](/morosidad-gastos-comunes) — que es plata que el resto de los vecinos termina poniendo.',
          'La transparencia también baja costos: cuando los copropietarios ven cada gasto con su factura, las decisiones de la [asamblea](/asamblea-de-copropietarios) se toman con datos y sin desconfianza. Con Habitia, cada factura del edificio se procesa con IA y queda visible para todos, y cada residente consulta su estado de cuenta al momento.',
        ],
      },
    ],
    faqs: [
      {
        q: '¿Los gastos comunes los paga el propietario o el inquilino?',
        a: 'Depende de lo que establezca el contrato de alquiler. Lo habitual en Uruguay es que el inquilino pague los gastos comunes ordinarios (los del funcionamiento mensual) y el propietario los extraordinarios (obras y mejoras).',
      },
      {
        q: '¿Los honorarios de la administración van dentro de los gastos comunes?',
        a: 'Sí: son un rubro más de la liquidación mensual. En nuestra guía de honorarios explicamos cuánto cobra una administración y qué debe incluir ese precio.',
      },
      {
        q: '¿Qué pasa si un vecino no paga?',
        a: 'La deuda se gestiona con recargos y cobranza, y en última instancia por vía judicial. Lo explicamos en detalle en la guía sobre morosidad en gastos comunes.',
      },
    ],
  },
  '/morosidad-gastos-comunes': {
    h1: 'Morosidad en gastos comunes: qué puede hacer el edificio',
    lead: 'La morosidad es el problema número uno de los edificios: cuando algunas unidades no pagan, el costo de mantener el edificio recae sobre los vecinos que sí lo hacen. Esta guía explica qué herramientas tiene el edificio frente a los deudores de gastos comunes en Uruguay, y — más importante — cómo prevenir la mora antes de que se acumule.',
    fecha: '2026-08-17',
    sections: [
      {
        heading: 'Por qué la morosidad golpea a todo el edificio',
        body: [
          'Los gastos del edificio no bajan porque alguien deje de pagar: la limpieza, el ascensor y los seguros se pagan igual. Cada peso que falta lo cubren los demás copropietarios — o se recorta el mantenimiento, que es la forma más cara de "ahorrar". Un edificio con mora alta además pierde valor: es una señal de mala gestión que los compradores detectan.',
        ],
      },
      {
        heading: 'Las herramientas frente al deudor',
        body: ['De menor a mayor intensidad, el camino ordenado:'],
        numbered: true,
        list: [
          'Recordatorios tempranos y claros: gran parte de la mora inicial es olvido o desorden, no insolvencia.',
          'Estado de cuenta transparente: el deudor tiene que poder ver exactamente cuánto debe y de dónde sale el número.',
          'Recargos por mora e intereses, según lo que prevea el reglamento dentro del marco legal uruguayo.',
          'Convenios de pago por escrito, con cuotas realistas y seguimiento.',
          'Vía judicial: en Uruguay la deuda de gastos comunes tiene un camino judicial relativamente ágil. Es el último recurso — con asesoría legal — pero existe y funciona.',
        ],
      },
      {
        heading: 'Cómo se previene la mora',
        body: [
          'La mora se combate antes de que exista: avisos automáticos antes y después del vencimiento, facilidades de pago que no dependan de ir a un local, y una liquidación mensual que se entienda — la desconfianza en los números es una de las excusas más comunes para no pagar.',
          'Con Habitia los recordatorios salen solos por WhatsApp, cada residente ve su estado de cuenta en la app, y la administración tiene un tablero de morosidad por edificio para actuar temprano. Los [gastos comunes claros](/gastos-comunes-que-incluyen) cobran mejor.',
        ],
      },
    ],
    faqs: [
      {
        q: '¿Se le pueden cortar servicios a un deudor?',
        a: 'Los servicios esenciales no. El uso de amenities y espacios opcionales puede limitarse si el reglamento de copropiedad lo prevé. Antes de aplicar cualquier medida, revisá el reglamento y asesorate.',
      },
      {
        q: '¿Cuánto recargo se le puede cobrar a un moroso?',
        a: 'Lo define el reglamento dentro del marco legal uruguayo, que regula los intereses y reajustes aplicables a deudas de gastos comunes. La asamblea no puede fijar recargos por fuera de ese marco.',
      },
      {
        q: '¿La deuda de gastos comunes prescribe o se pierde al vender?',
        a: 'La deuda sigue al inmueble en la práctica de las compraventas: los escribanos exigen el certificado de estar al día con los gastos comunes antes de escriturar. Por eso la deuda casi siempre se paga al vender.',
      },
    ],
  },
  '/asamblea-de-copropietarios': {
    h1: 'Asamblea de copropietarios: guía práctica',
    lead: 'La asamblea es el órgano donde el edificio decide: presupuesto, administración, obras, reglas de convivencia. Bien llevada, ordena la vida del edificio; mal llevada, es una fuente de conflictos y decisiones que nunca se ejecutan. Esta guía explica cómo funciona una asamblea de copropietarios en Uruguay y cómo hacer que las decisiones realmente pasen.',
    fecha: '2026-08-17',
    sections: [
      {
        heading: 'Qué se decide en asamblea',
        body: ['Los temas típicos que requieren asamblea:'],
        list: [
          'Aprobar el presupuesto y la rendición de cuentas anual.',
          'Designar o cesar a la administración (lo explicamos paso a paso en la [guía para cambiar de administración](/como-cambiar-de-administracion)).',
          'Obras y mejoras del edificio, y cómo se financian.',
          'Reglas de convivencia, uso de amenities y sanciones.',
        ],
      },
      {
        heading: 'Convocatoria y quórum',
        body: [
          'El reglamento de copropiedad define cómo se convoca (plazos, medio de notificación) y qué quórum se necesita para sesionar. La asamblea ordinaria se hace al menos una vez al año — típicamente para aprobar cuentas y presupuesto — y las extraordinarias se convocan cuando hay un tema puntual que resolver.',
          'Si no se alcanza el quórum, el reglamento suele prever una segunda convocatoria con exigencias menores. El error clásico es convocar mal (fuera de plazo o sin notificar a todos): decisiones tomadas en asambleas mal convocadas pueden impugnarse.',
        ],
      },
      {
        heading: 'Mayorías: quién decide qué',
        body: [
          'No todos los temas se deciden igual. Las cuestiones ordinarias suelen resolverse por mayoría simple de los presentes, mientras que los temas mayores — obras importantes, cambios al reglamento — exigen mayorías especiales que el propio reglamento y la ley establecen. Antes de someter un tema grande a votación, verificá qué mayoría exige: una obra aprobada con la mayoría equivocada es un problema legal en puerta.',
        ],
      },
      {
        heading: 'El acta: donde las decisiones se vuelven reales',
        body: [
          'Lo que no está en el acta, no pasó. El acta registra quiénes participaron, qué se votó y qué se resolvió — y es el documento que después permite exigir el cumplimiento (a la administración, a un proveedor o a un vecino). Guardarlas ordenadas y accesibles es parte de la salud institucional del edificio.',
          'Con Habitia las actas quedan digitalizadas y accesibles para todos los copropietarios, y las resoluciones se convierten en tareas con seguimiento — la diferencia entre "se habló" y "se hizo".',
        ],
      },
    ],
    faqs: [
      {
        q: '¿Puede votar un inquilino en la asamblea?',
        a: 'El voto corresponde a los propietarios. Un inquilino puede participar si el propietario le da poder de representación por escrito.',
      },
      {
        q: '¿Las decisiones de la asamblea obligan a los que no fueron?',
        a: 'Sí: las decisiones válidamente adoptadas obligan a todos los copropietarios, hayan asistido o no. Por eso conviene participar — o dar poder a alguien de confianza.',
      },
      {
        q: '¿Cada cuánto hay que hacer asamblea?',
        a: 'La ordinaria al menos una vez al año. Las extraordinarias, cuando un tema lo amerita — por ejemplo, una obra urgente o un cambio de administración.',
      },
    ],
  },
  '/administracion-de-edificios-montevideo': {
    h1: 'Administración de edificios en Montevideo',
    lead: 'Elegir la administración de un edificio define cómo se vive en él: cuánta transparencia hay en los gastos comunes, qué tan rápido se resuelven los problemas y cuánto vale cada unidad con el tiempo. Esta guía explica qué tiene que hacer una administración profesional y cómo la inteligencia artificial está cambiando la forma de administrar edificios en Montevideo.',
    sections: [
      {
        heading: 'Qué hace una administración de edificios profesional',
        body: [
          'La administración es la responsable de que el edificio funcione todos los días y de que la plata de los copropietarios esté bien gestionada. Como mínimo, tiene que encargarse de:',
        ],
        list: [
          'Liquidar y cobrar los gastos comunes, y rendir cuentas todos los meses.',
          'Pagar en fecha a proveedores y servicios: limpieza, ascensores, UTE, OSE, seguros.',
          'Recibir incidencias, coordinar reparaciones y hacerles seguimiento hasta que se resuelven.',
          'Convocar asambleas, llevar las actas y ejecutar lo que se resuelve.',
          'Gestionar al personal del edificio cuando lo hay (portería, limpieza).',
          'Cuidar el cumplimiento: seguros vigentes, habilitaciones y obligaciones laborales y tributarias.',
        ],
      },
      {
        heading: 'Los problemas más comunes con las administraciones tradicionales',
        body: [
          'Las quejas que más se repiten entre copropietarios en Montevideo son casi siempre las mismas: rendiciones que no se entienden o llegan tarde, no saber en qué se gasta la plata del edificio, incidencias que se pierden en cadenas de WhatsApp y administradores imposibles de contactar cuando hay un problema.',
          'Nada de eso es inevitable. En general no es mala fe: son herramientas viejas — planillas, papel, mail — que no escalan cuando la administración maneja muchos edificios a la vez.',
        ],
      },
      {
        heading: 'Administración con IA: qué cambia',
        body: [
          'Habitia incorpora a Habi, un agente de inteligencia artificial que trabaja dentro de la plataforma y reduce el trabajo manual que hace lenta a una administración tradicional — para que el equipo se dedique a lo importante, que es donde hace la diferencia:',
        ],
        list: [
          'Cada factura del edificio se procesa con IA y queda registrada y visible: los copropietarios pueden ver en qué se gasta, mes a mes.',
          'Los residentes consultan sus gastos comunes o reportan un problema a cualquier hora, y Habi les responde al momento, todos los días del año.',
          'Las incidencias quedan registradas con estado y responsable: nada se pierde en un chat.',
          'El administrador ve la foto real de cada edificio — morosidad, gastos, incidencias abiertas — en un panel, sin armar planillas.',
        ],
      },
      {
        heading: 'Cómo trabaja Habitia en Montevideo',
        body: [
          'Habitia combina una administración profesional con una plataforma propia con IA. Los copropietarios tienen transparencia total sobre los gastos comunes, los residentes tienen respuesta inmediata, y la comisión tiene reportes claros para decidir.',
          'Si tu edificio ya tiene administración y no los conforma, el cambio es más simple de lo que parece: lo explicamos paso a paso en nuestra [guía para cambiar de administración](/como-cambiar-de-administracion), y nosotros nos encargamos del traspaso.',
        ],
      },
    ],
    faqs: [
      {
        q: '¿Qué hace la administración de un edificio?',
        a: 'Gestiona el día a día del edificio: liquida y cobra los gastos comunes, paga a proveedores, atiende incidencias, convoca asambleas, administra al personal y rinde cuentas a los copropietarios.',
      },
      {
        q: '¿Cuánto cobra una administración de edificios en Montevideo?',
        a: 'Depende del tamaño del edificio, los amenities y los servicios incluidos. Las modalidades más comunes son por unidad, porcentaje del presupuesto o tarifa fija. Lo explicamos en detalle en nuestra [guía de honorarios de administración](/cuanto-cobra-una-administracion).',
      },
      {
        q: '¿Qué diferencia a una administración con IA?',
        a: 'La tecnología reduce el trabajo manual — las facturas se procesan automáticamente, los residentes tienen respuesta 24/7 con el agente Habi y toda la información queda visible y auditable — para que el administrador se dedique a lo importante: decidir, resolver y estar cerca del edificio, que es donde hace la diferencia.',
      },
      {
        q: '¿Dónde trabaja Habitia?',
        a: 'En todo el Uruguay. Habitia administra edificios en Montevideo y en el resto del país, tanto edificios chicos como torres con amenities — la plataforma funciona igual en cualquier ciudad.',
      },
    ],
  },
  '/cuanto-cobra-una-administracion': {
    h1: '¿Cuánto cobra una administración de edificios en Uruguay?',
    lead: 'El precio es una de las primeras preguntas de cualquier comisión o grupo de copropietarios que evalúa contratar o cambiar de administración. La respuesta corta: depende de cómo cobre y de qué incluya. Esta guía explica las modalidades de cobro que se usan en Uruguay, qué tiene que estar incluido en el honorario y qué señales mirar para saber si estás pagando bien.',
    sections: [
      {
        heading: 'Las tres formas de cobrar más comunes',
        body: ['En Uruguay vas a encontrar principalmente tres modalidades:'],
        list: [
          'Por unidad: un precio por apartamento por mes. Es la más transparente y la más fácil de comparar entre administraciones; el costo acompaña el tamaño del edificio.',
          'Porcentaje del presupuesto: la administración cobra un porcentaje de lo que gasta el edificio. Ojo con el incentivo: cuanto más gasta el edificio, más cobra la administración.',
          'Tarifa fija: un monto mensual pactado, habitual en edificios chicos con pocos servicios.',
        ],
      },
      {
        heading: 'Qué factores mueven el precio',
        body: ['Dos edificios de la misma cuadra pueden pagar honorarios muy distintos. Lo que más pesa:'],
        list: [
          'La cantidad de unidades: a más unidades, el precio por unidad suele bajar.',
          'Amenities y equipamiento: piscina, barbacoa, gimnasio y ascensores suman mantenimiento y coordinación.',
          'Personal propio: si el edificio tiene portería o limpieza contratada, la administración gestiona sueldos, licencias y obligaciones laborales.',
          'El estado del edificio: un edificio con años de mantenimiento postergado exige más gestión.',
          'La cobranza: edificios con mora alta requieren gestión de deudores más activa.',
        ],
      },
      {
        heading: 'Qué tiene que estar incluido en el honorario',
        body: ['Antes de comparar precios, pedí por escrito qué incluye cada cotización. Un honorario razonable tiene que cubrir:'],
        list: [
          'Liquidación mensual de los gastos comunes y estado de cuenta por unidad.',
          'Rendición de cuentas clara, todos los meses.',
          'Pagos a proveedores y servicios del edificio.',
          'Atención a copropietarios y residentes, y gestión de incidencias.',
          'La asamblea ordinaria anual y el seguimiento de lo resuelto.',
        ],
        // Nota comercial: acá se pueden publicar rangos de precio de mercado
        // cuando el usuario lo apruebe (coordinar con el cotizador).
      },
      {
        heading: 'Señales de alerta al comparar cotizaciones',
        body: [
          'Un precio muy por debajo del mercado suele compensarse después: extras por cada trámite, asambleas extraordinarias cobradas aparte o rendiciones que nunca llegan. Las señales clásicas: honorarios que suben sin aviso, cotizaciones "sin IVA" que sorprenden en la primera liquidación, y falta de detalle sobre qué incluye el servicio.',
          'La regla simple: el honorario más barato no es el que menos cuesta, es el que menos gestiona.',
        ],
      },
      {
        heading: 'Cómo cobra Habitia',
        body: [
          'Habitia cobra por unidad, con una tarifa escalonada según el tamaño del edificio y con IVA incluido: el precio que te cotizamos es el precio final, sin sorpresas ni extras ocultos.',
          'La cotización exacta para tu edificio la tenés en dos minutos con nuestro [cotizador online](/cotizador): contanos cuántas unidades y servicios tiene, y te la enviamos por WhatsApp y email.',
        ],
      },
    ],
    faqs: [
      {
        q: '¿Los honorarios de administración llevan IVA?',
        a: 'Sí, los honorarios de administración están gravados con IVA. Al comparar cotizaciones confirmá siempre si el precio es final o si hay que sumarle el impuesto. En Habitia el precio cotizado es final, con IVA incluido.',
      },
      {
        q: '¿Quién paga los honorarios de la administración?',
        a: 'Los copropietarios, como un rubro más dentro de los gastos comunes del edificio.',
      },
      {
        q: '¿Honorarios y gastos comunes son lo mismo?',
        a: 'No. El honorario es lo que cobra la administración por su trabajo. Los gastos comunes incluyen además la limpieza, los servicios, el mantenimiento, los seguros y los fondos del edificio.',
      },
    ],
  },
  '/como-cambiar-de-administracion': {
    h1: 'Cómo cambiar la administración de tu edificio',
    lead: 'Cambiar de administración parece complicado, y por eso muchos edificios se quedan años con un servicio que no los conforma. En la práctica es un proceso ordenado: se decide en asamblea, se documenta el traspaso y la nueva administración se encarga del resto. Esta es la guía paso a paso para hacerlo bien en Uruguay.',
    sections: [
      {
        heading: 'Cuándo conviene plantearse el cambio',
        body: ['Algunas señales de que el edificio necesita otra administración:'],
        list: [
          'Las rendiciones no llegan, llegan tarde o no se entienden.',
          'La mora crece y nadie la gestiona.',
          'Las incidencias se reportan y quedan sin respuesta.',
          'Es imposible comunicarse con el administrador.',
          'Los gastos comunes suben y nadie explica por qué.',
        ],
      },
      {
        heading: 'El paso a paso',
        body: ['El cambio se resuelve en asamblea de copropietarios. El camino ordenado:'],
        numbered: true,
        list: [
          'Sondeá a los vecinos: el cambio necesita respaldo de los copropietarios, empezá por conversarlo.',
          'Revisá el reglamento de copropiedad: ahí está definido cómo se convoca la asamblea y qué mayorías se necesitan. En general, la designación y el cese del administrador se resuelven por mayoría en asamblea.',
          'Pedí cotizaciones y compará qué incluye cada una (nuestra [guía de honorarios](/cuanto-cobra-una-administracion) te sirve de checklist).',
          'Convocá la asamblea según el reglamento, incluyendo el cambio de administración en el orden del día.',
          'Votá y dejá todo en acta: el cese de la administración actual y la designación de la nueva.',
          'Notificá a la administración saliente y acordá una fecha de traspaso.',
        ],
      },
      {
        heading: '¿Dudas con tu reglamento? Te asesoramos gratis',
        body: [
          'Cada reglamento de copropiedad es distinto, y el paso más delicado del cambio es convocar la asamblea como corresponde: plazos, forma de notificación y mayorías. Si querés ir a lo seguro, [envianos tu reglamento de copropiedad por WhatsApp](https://wa.me/59899761810) y te asesoramos sin costo sobre cómo citar la asamblea correctamente — sin compromiso de contratarnos.',
        ],
      },
      {
        heading: 'El traspaso: qué documentación exigir',
        body: ['La administración saliente debe rendir cuentas hasta el último día y entregar todo lo del edificio. Como mínimo:'],
        list: [
          'Libros de actas de asambleas.',
          'Rendiciones y estados de cuenta al día.',
          'Saldos y acceso a las cuentas bancarias del edificio.',
          'Padrón de copropietarios y estado de deudas por unidad.',
          'Contratos vigentes: proveedores, personal, seguros.',
          'Documentación del edificio: planos, habilitaciones, garantías de obras.',
        ],
      },
      {
        heading: 'Cómo acompaña Habitia la transición',
        body: [
          'Cuando un edificio pasa a Habitia, el traspaso lo manejamos nosotros con un checklist de documentación, la carga del edificio y sus unidades en la plataforma, y la invitación a todos los residentes. Nadie deja de cobrar ni de pagar en el medio: la operación del edificio sigue sin cortes.',
          'Desde el primer mes, los copropietarios ven los gastos comunes y las incidencias en la plataforma, con el historial digitalizado desde el arranque.',
        ],
      },
    ],
    faqs: [
      {
        q: '¿Qué mayoría se necesita para cambiar de administración?',
        a: 'La define el reglamento de copropiedad de cada edificio. En general, la designación y el cese del administrador se resuelven por mayoría en asamblea de copropietarios. Si nos enviás tu reglamento por WhatsApp, te lo confirmamos sin costo.',
      },
      {
        q: '¿Cuánto demora el cambio?',
        a: 'Lo que demore convocar la asamblea más el traspaso de documentación. Con la documentación en orden, se mide en semanas, no en meses.',
      },
      {
        q: '¿Se pierde el historial del edificio al cambiar?',
        a: 'No debería: exigí rendiciones, actas y saldos en el traspaso. Con Habitia, además, todo el historial queda digitalizado y accesible en la plataforma.',
      },
    ],
  },
  '/software-de-administracion-de-edificios': {
    h1: 'Software de administración de edificios',
    lead: 'Administrar edificios con planillas, mails y grupos de WhatsApp funciona hasta que deja de funcionar: rendiciones que llevan días, incidencias que se pierden y copropietarios que desconfían. Esta guía repasa qué tiene que tener un buen software de administración de edificios y qué cambia cuando le sumás inteligencia artificial.',
    sections: [
      {
        heading: 'Funcionalidades imprescindibles',
        body: ['Un software de administración de edificios completo tiene que resolver, como mínimo:'],
        list: [
          'Gastos comunes: liquidación, cobranza y estado de cuenta por unidad.',
          'Incidencias con estado y responsable, desde el reporte hasta la resolución.',
          'Comunicación: novedades y avisos que llegan a todos los residentes, con registro.',
          'Documentos del edificio y de cada unidad en un solo lugar.',
          'Reportes: morosidad, gastos y actividad de cada edificio en tiempo real.',
          'Portal para residentes, desde el celular o la computadora.',
        ],
      },
      {
        heading: 'Qué suma la inteligencia artificial',
        body: [
          'La diferencia de Habitia es Habi, un agente de IA integrado a la plataforma. Habi procesa las facturas del edificio automáticamente — proveedor, fecha, importe — sin tipeo manual, y atiende las consultas de los residentes a cualquier hora: cuánto debo, qué pasó con mi reclamo, cuándo vence el pago.',
          'El resultado: el equipo administrativo deja de cargar datos y contestar lo repetido, y dedica el tiempo a decidir y resolver.',
        ],
      },
      {
        heading: 'Para administradoras: escalar la cartera sin sumar back office',
        body: [
          'Para una administradora profesional, cada edificio nuevo suele significar más carga administrativa. Con Habitia, sumar edificios no exige sumar gente en la misma proporción: cada edificio tiene su panel, las facturas se procesan solas y los residentes se autoatienden con Habi.',
          'La vista consolidada muestra morosidad, incidencias abiertas y gastos de toda la cartera en una sola pantalla.',
        ],
      },
      {
        heading: 'Habitia: plataforma + administración',
        body: [
          'Habitia se puede usar de dos maneras: como [administración con IA](/propietario) — Habitia administra tu edificio de punta a punta — o como [software para administradoras profesionales](/administradora) que quieren modernizar su operación.',
          'En los dos casos, la base es la misma: gastos comunes claros, incidencias con seguimiento y residentes atendidos 24/7.',
        ],
      },
    ],
    faqs: [
      {
        q: '¿Sirve para administradoras con muchos edificios?',
        a: 'Sí. Habitia es multi-edificio: cada edificio tiene su panel y la administradora ve la cartera completa consolidada, con morosidad, gastos e incidencias por edificio.',
      },
      {
        q: '¿Los residentes también usan la plataforma?',
        a: 'Sí. Los residentes consultan sus gastos comunes, reportan incidencias, reciben novedades y le preguntan a Habi lo que necesiten, a cualquier hora.',
      },
      {
        q: '¿Cuánto lleva implementarlo?',
        a: 'La carga inicial del edificio y sus unidades más la invitación a los residentes. Habitia acompaña el onboarding completo, incluido el traspaso desde la administración anterior.',
      },
    ],
  },
}

export const GUIAS = GUIAS_META.map((m) => ({ ...m, content: GUIAS_CONTENT[m.path] })).filter((g) => g.content);

export const guiaUrl = (path) => `https://habitia.ai${path}`;

const esc = (s) =>
  String(s == null ? "" : s)
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

const LINK_PATTERN = /\[([^\]]+)\]\(([^)]+)\)/g;

// Links estilo markdown del contenido → <a> absolutos a habitia.ai. Si el texto
// trae un wa.me hardcodeado (número general de Habitia), se redirige al del
// comercial cuando hay uno.
function rich(text, waAgente) {
  return esc(text).replace(LINK_PATTERN, (_m, label, href) => {
    let url = href.startsWith("/") ? `https://habitia.ai${href}` : href;
    if (waAgente && url.startsWith("https://wa.me/")) url = waAgente;
    return `<a href="${url}" style="color:#0080ff; text-decoration:underline; text-decoration-color:rgba(0,128,255,0.4); text-underline-offset:3px;">${label}</a>`;
  });
}

// Viñetas como DOM real (no pseudo-elementos: html2canvas no los captura).
const check = `<span style="flex-shrink:0; width:22px; height:22px; margin-top:1px; border-radius:999px; background:rgba(0,128,255,0.1); color:#0080ff; display:inline-flex; align-items:center; justify-content:center; font-size:13px; line-height:1;">✓</span>`;
const num = (i) => `<span style="flex-shrink:0; width:26px; height:26px; border-radius:999px; background:#0080ff; color:#ffffff; display:inline-flex; align-items:center; justify-content:center; font-size:13px; line-height:1;">${i}</span>`;

/**
 * HTML autocontenido de la guía con el diseño de la web (MoneyPage.module.css):
 * hero con degradé celeste, títulos azules, checks, pasos numerados, FAQ en
 * cards y CTA con degradé. Para preview, export PDF o adjuntar por mail.
 */
export function buildGuiaHtml(guia, agente = {}) {
  const c = guia.content;
  const telDigits = String(agente.tel || "").replace(/\D/g, "");
  const waAgente = telDigits ? `https://wa.me/${telDigits}` : null;
  const aName = esc((agente.name || "").trim());

  const sections = (c.sections || []).map((s) => {
    const parrafos = (s.body || []).map((p) => `<p style="font-size:17px; line-height:1.7; color:#0f172a; margin:0 0 16px;">${rich(p, waAgente)}</p>`).join("");
    let lista = "";
    if (s.list) {
      const items = s.list.map((item, i) => `
        <li style="display:flex; gap:14px; align-items:flex-start; font-size:17px; line-height:1.6; color:#0f172a;">${s.numbered ? num(i + 1) : check}<span style="min-width:0;">${rich(item, waAgente)}</span></li>`).join("");
      lista = `<ul style="list-style:none; margin:6px 0 0; padding:0; display:flex; flex-direction:column; gap:${s.numbered ? 14 : 12}px;">${items}</ul>`;
    }
    return `
    <section style="margin-top:44px;">
      <h2 style="font-size:25px; font-weight:400; line-height:1.25; letter-spacing:-0.02em; color:#0080ff; margin:0 0 16px;">${esc(s.heading)}</h2>
      ${parrafos}${lista}
    </section>`;
  }).join("");

  const faqs = (c.faqs || []).length ? `
    <section style="margin-top:44px;">
      <h2 style="font-size:25px; font-weight:400; letter-spacing:-0.02em; color:#0080ff; margin:0 0 16px;">Preguntas frecuentes</h2>
      <div style="display:flex; flex-direction:column; gap:14px;">
        ${c.faqs.map((f) => `
        <div style="border:1px solid #e2e8f0; border-radius:16px; padding:18px 22px; background:#ffffff;">
          <h3 style="font-size:17.5px; font-weight:400; color:#0f172a; line-height:1.4; margin:0 0 7px;">${esc(f.q)}</h3>
          <p style="font-size:16px; line-height:1.65; color:#475569; margin:0;">${rich(f.a, waAgente)}</p>
        </div>`).join("")}
      </div>
    </section>` : "";

  const ctaWhats = waAgente
    ? `<a href="${waAgente}" style="display:inline-flex; align-items:center; justify-content:center; min-height:40px; padding:0 24px; border-radius:999px; border:1px solid rgba(255,255,255,0.65); color:#ffffff; font-size:15px; text-decoration:none;">Escribinos por WhatsApp</a>`
    : "";

  const contacto = aName
    ? `<p style="margin:28px 0 0; font-size:15px; color:#0f172a;">${aName}<span style="color:#475569;"> · ${esc(agente.cargo || "Comercial")} · Habitia${agente.tel ? ` · ${esc(agente.tel)}` : ""}</span></p>`
    : "";

  const relacionadas = GUIAS.filter((g) => g.path !== guia.path).map((g) =>
    `<a href="https://habitia.ai${g.path}" style="display:flex; align-items:center; justify-content:space-between; gap:16px; border:1px solid #e2e8f0; border-radius:16px; padding:16px 20px; background:#ffffff; color:#0f172a; font-size:16px; line-height:1.4; text-decoration:none;">${esc(g.navLabel)}<span style="color:#0080ff;">→</span></a>`
  ).join("");

  return `<!DOCTYPE html>
<html lang="es">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<title>${esc(c.h1)} — Guías Habitia</title>
<style>
  @font-face { font-family: 'FT Habit'; font-weight: 350; src: url('${ftBook}') format('opentype'); font-display: swap; }
  @font-face { font-family: 'FT Habit'; font-weight: 400; src: url('${ftRegular}') format('opentype'); font-display: swap; }
  @font-face { font-family: 'FT Habit'; font-weight: 500; src: url('${ftMedium}') format('opentype'); font-display: swap; }
  body { margin:0; padding:0; }
</style>
</head>
<body style="margin:0; background:#ffffff; font-family:'FT Habit','Helvetica Neue',Helvetica,Arial,sans-serif; -webkit-font-smoothing:antialiased;">
  <div style="background:linear-gradient(180deg, #eef6ff 0%, #ffffff 100%); padding:44px 24px 40px;">
    <div style="max-width:860px; margin:0 auto; display:flex; flex-direction:column; gap:16px;">
      <p style="font-size:17px; letter-spacing:0.02em; color:#0080ff; margin:0;">Guías Habitia</p>
      <h1 style="font-size:36px; font-weight:400; line-height:1.16; letter-spacing:-0.02em; color:#0f172a; margin:0;">${esc(c.h1)}</h1>
      <p style="font-size:18px; line-height:1.65; color:#475569; margin:0; max-width:780px;">${rich(c.lead, waAgente)}</p>
    </div>
  </div>
  <div style="max-width:860px; margin:0 auto; padding:8px 24px 64px;">
    ${sections}
    ${faqs}
    <section style="margin-top:52px; border-radius:20px; padding:36px; background:linear-gradient(135deg, #0080ff 0%, #0066cc 100%); box-shadow:0 24px 56px rgba(0,128,255,0.24); color:#ffffff;">
      <p style="font-size:25px; line-height:1.3; letter-spacing:-0.02em; margin:0 0 7px;">¿Querés ver cómo Habitia administra tu edificio con IA?</p>
      <p style="font-size:17px; line-height:1.5; color:rgba(255,255,255,0.9); margin:0 0 24px;">Gastos comunes claros y residentes atendidos 24/7.</p>
      <div style="display:flex; flex-wrap:wrap; gap:12px;">
        <a href="https://habitia.ai/cotizador" style="display:inline-flex; align-items:center; justify-content:center; min-height:40px; padding:0 24px; border-radius:999px; background:#ffffff; color:#0080ff; font-size:15px; text-decoration:none; box-shadow:0 2px 10px rgba(0,0,0,0.12);">Cotizá tu edificio online</a>
        ${ctaWhats}
      </div>
    </section>
    <section style="margin-top:52px;">
      <h2 style="font-size:25px; font-weight:400; letter-spacing:-0.02em; color:#0080ff; margin:0 0 16px;">Guías relacionadas</h2>
      <div style="display:grid; grid-template-columns:repeat(auto-fit, minmax(260px, 1fr)); gap:14px;">${relacionadas}</div>
    </section>
    ${contacto}
    <p style="margin:10px 0 0; font-size:13px; color:#64748b;">habitia.ai · La forma moderna de administrar edificios · <a href="https://habitia.ai${guia.path}" style="color:#0080ff; text-decoration:none;">versión online de esta guía</a></p>
  </div>
</body>
</html>`;
}
